import React, { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Target, BookOpen, TrendingUp, Trophy, Flame, ArrowRight, Sparkles, Calendar, Users, Zap, Quote, Eye, Plus, BarChart3, Bell, Award, Star, Radio, GraduationCap, Coffee, Heart, Pencil, BookMarked, Lock, Clock, MessageCircle, ExternalLink, Bookmark } from 'lucide-react';
import { getExamIcon } from '../lib/examIcons';
import { dailyChallengeAPI, testAPI } from '../lib/api';
import { toast } from 'sonner';
import { DISCORD_INVITE_URL } from '../config/discord';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [stats, setStats] = useState({ testsToday: 0, accuracy: 0, totalTests: 0 });
  const [loading, setLoading] = useState(true);
  const [motivationQuote, setMotivationQuote] = useState(null);
  const [selectedExam, setSelectedExam] = useState(localStorage.getItem('selectedExam') || null);

  const motivationQuotes = [
    // Classic Motivation
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
    { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
    { text: "It does not matter how slowly you go as long as you do not stop.", author: "Confucius" },
    { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
    { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
    { text: "Quality is not an act, it is a habit.", author: "Aristotle" },
    
    // Education & Learning
    { text: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela" },
    { text: "The beautiful thing about learning is that nobody can take it away from you.", author: "B.B. King" },
    { text: "Live as if you were to die tomorrow. Learn as if you were to live forever.", author: "Mahatma Gandhi" },
    { text: "The more that you read, the more things you will know. The more that you learn, the more places you'll go.", author: "Dr. Seuss" },
    { text: "Education is not preparation for life; education is life itself.", author: "John Dewey" },
    { text: "The roots of education are bitter, but the fruit is sweet.", author: "Aristotle" },
    { text: "Learning is not attained by chance, it must be sought for with ardor and attended to with diligence.", author: "Abigail Adams" },
    { text: "An investment in knowledge pays the best interest.", author: "Benjamin Franklin" },
    { text: "The capacity to learn is a gift; the ability to learn is a skill; the willingness to learn is a choice.", author: "Brian Herbert" },
    
    // Hard Work & Persistence
    { text: "Hard work beats talent when talent doesn't work hard.", author: "Tim Notke" },
    { text: "There are no secrets to success. It is the result of preparation, hard work, and learning from failure.", author: "Colin Powell" },
    { text: "Success is the sum of small efforts repeated day in and day out.", author: "Robert Collier" },
    { text: "The difference between ordinary and extraordinary is that little extra.", author: "Jimmy Johnson" },
    { text: "Perseverance is not a long race; it is many short races one after the other.", author: "Walter Elliot" },
    { text: "It's not that I'm so smart, it's just that I stay with problems longer.", author: "Albert Einstein" },
    { text: "Genius is 1% inspiration and 99% perspiration.", author: "Thomas Edison" },
    { text: "The only place where success comes before work is in the dictionary.", author: "Vidal Sassoon" },
    { text: "There is no substitute for hard work.", author: "Thomas Edison" },
    { text: "I find that the harder I work, the more luck I seem to have.", author: "Thomas Jefferson" },
    
    // Exam & Study Specific
    { text: "Don't let what you cannot do interfere with what you can do.", author: "John Wooden" },
    { text: "The expert in anything was once a beginner.", author: "Helen Hayes" },
    { text: "Every expert was once a beginner. Every pro was once an amateur.", author: "Robin Sharma" },
    { text: "You don't have to be great to start, but you have to start to be great.", author: "Zig Ziglar" },
    { text: "A little progress each day adds up to big results.", author: "Satya Nani" },
    { text: "Study while others are sleeping; work while others are loafing.", author: "William Arthur Ward" },
    { text: "The pain of studying is temporary. The pain of not knowing is forever.", author: "Unknown" },
    { text: "Push yourself because no one else is going to do it for you.", author: "Unknown" },
    { text: "Your grades don't define you, but your effort does.", author: "Unknown" },
    { text: "Studying is like climbing a mountain. It's hard, but the view from the top is worth it.", author: "Unknown" },
    
    // Focus & Discipline
    { text: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
    { text: "Focus on being productive instead of busy.", author: "Tim Ferriss" },
    { text: "The successful warrior is the average man, with laser-like focus.", author: "Bruce Lee" },
    { text: "Concentrate all your thoughts upon the work at hand.", author: "Alexander Graham Bell" },
    { text: "Where focus goes, energy flows.", author: "Tony Robbins" },
    { text: "Don't be afraid to give up the good to go for the great.", author: "John D. Rockefeller" },
    { text: "Stay focused, go after your dreams and keep moving toward your goals.", author: "LL Cool J" },
    { text: "Lack of direction, not lack of time, is the problem.", author: "Zig Ziglar" },
    
    // Overcoming Challenges
    { text: "The only impossible journey is the one you never begin.", author: "Tony Robbins" },
    { text: "Challenges are what make life interesting. Overcoming them is what makes life meaningful.", author: "Joshua Marine" },
    { text: "I have not failed. I've just found 10,000 ways that won't work.", author: "Thomas Edison" },
    { text: "Failure is simply the opportunity to begin again, this time more intelligently.", author: "Henry Ford" },
    { text: "Our greatest glory is not in never falling, but in rising every time we fall.", author: "Confucius" },
    { text: "Fall seven times, stand up eight.", author: "Japanese Proverb" },
    { text: "Difficulties in life are intended to make us better, not bitter.", author: "Dan Reeves" },
    { text: "The greater the obstacle, the more glory in overcoming it.", author: "Molière" },
    { text: "Tough times never last, but tough people do.", author: "Robert H. Schuller" },
    
    // Dreams & Goals
    { text: "A goal without a plan is just a wish.", author: "Antoine de Saint-Exupéry" },
    { text: "Set your goals high, and don't stop till you get there.", author: "Bo Jackson" },
    { text: "The distance between your dreams and reality is called action.", author: "Unknown" },
    { text: "Dreams don't work unless you do.", author: "John C. Maxwell" },
    { text: "Your time is limited, don't waste it living someone else's life.", author: "Steve Jobs" },
    { text: "If you want something you've never had, you must be willing to do something you've never done.", author: "Thomas Jefferson" },
    { text: "Don't limit your challenges. Challenge your limits.", author: "Unknown" },
    { text: "Be so good they can't ignore you.", author: "Steve Martin" },
    
    // Self-Belief
    { text: "Whether you think you can or you think you can't, you're right.", author: "Henry Ford" },
    { text: "You are braver than you believe, stronger than you seem, and smarter than you think.", author: "A.A. Milne" },
    { text: "Don't be pushed around by the fears in your mind. Be led by the dreams in your heart.", author: "Roy T. Bennett" },
    { text: "Believe in yourself and all that you are. Know that there is something inside you that is greater than any obstacle.", author: "Christian D. Larson" },
    { text: "Self-confidence is the first requisite to great undertakings.", author: "Samuel Johnson" },
    { text: "You have within you right now, everything you need to deal with whatever the world throws at you.", author: "Brian Tracy" },
    { text: "Trust yourself. You know more than you think you do.", author: "Benjamin Spock" },
    
    // IIT/NEET Specific Inspiration
    { text: "IIT is not the end of the road, but the beginning of a new journey.", author: "Unknown" },
    { text: "Every champion was once a contender who refused to give up.", author: "Rocky Balboa" },
    { text: "The competition is not with others. It's between you and yourself.", author: "Unknown" },
    { text: "Your rank doesn't define your worth. Your effort defines your character.", author: "Unknown" },
    { text: "One year of focused preparation can change your entire life.", author: "Unknown" },
    { text: "NEET is not just an exam, it's a gateway to saving lives.", author: "Unknown" },
    { text: "Every problem has a solution. You just have to be creative enough to find it.", author: "Unknown" },
    { text: "Physics teaches us that for every action, there's a reaction. Study hard, and success will follow.", author: "Unknown" },
    { text: "Chemistry is about bonds. Create a strong bond with your books.", author: "Unknown" },
    { text: "In Mathematics, there's no luck. Only preparation meets opportunity.", author: "Unknown" },
    { text: "Biology is the study of life. Your hard work today defines the life you'll live tomorrow.", author: "Unknown" },
    
    // Time Management
    { text: "Time is what we want most, but what we use worst.", author: "William Penn" },
    { text: "Don't count the days, make the days count.", author: "Muhammad Ali" },
    { text: "The bad news is time flies. The good news is you're the pilot.", author: "Michael Altshuler" },
    { text: "Yesterday is gone. Tomorrow has not yet come. We have only today. Let us begin.", author: "Mother Teresa" },
    { text: "Lost time is never found again.", author: "Benjamin Franklin" },
    { text: "Time management is life management.", author: "Robin Sharma" },
    
    // Consistency
    { text: "It's not what we do once in a while that shapes our lives. It's what we do consistently.", author: "Tony Robbins" },
    { text: "Small daily improvements over time lead to stunning results.", author: "Robin Sharma" },
    { text: "Success isn't always about greatness. It's about consistency.", author: "Dwayne Johnson" },
    { text: "Consistency is what transforms average into excellence.", author: "Unknown" },
    { text: "The secret of your future is hidden in your daily routine.", author: "Mike Murdock" },
    { text: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.", author: "Aristotle" },
    
    // Morning Motivation
    { text: "Every morning brings new potential, but if you dwell on the misfortunes of the day before, you tend to overlook tremendous opportunities.", author: "Harvey Mackay" },
    { text: "Today is a new day. Don't let your history interfere with your destiny.", author: "Steve Maraboli" },
    { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
    { text: "Wake up with determination. Go to bed with satisfaction.", author: "Unknown" },
    { text: "Each morning we are born again. What we do today is what matters most.", author: "Buddha" },
    
    // Indian Wisdom
    { text: "You have the right to work, but never to the fruit of work.", author: "Bhagavad Gita" },
    { text: "Arise, awake and stop not till the goal is reached.", author: "Swami Vivekananda" },
    { text: "Take risks in your life. If you win, you can lead. If you lose, you can guide.", author: "Swami Vivekananda" },
    { text: "In a day, when you don't come across any problems, you can be sure that you are travelling in a wrong path.", author: "Swami Vivekananda" },
    { text: "The greatest religion is to be true to your own nature. Have faith in yourselves.", author: "Swami Vivekananda" },
    { text: "You must be the change you wish to see in the world.", author: "Mahatma Gandhi" },
    { text: "Strength does not come from physical capacity. It comes from an indomitable will.", author: "Mahatma Gandhi" },
    { text: "First they ignore you, then they laugh at you, then they fight you, then you win.", author: "Mahatma Gandhi" },
    { text: "Dream, dream, dream. Dreams transform into thoughts and thoughts result in action.", author: "APJ Abdul Kalam" },
    { text: "If you fail, never give up because F.A.I.L. means First Attempt In Learning.", author: "APJ Abdul Kalam" },
    { text: "All of us do not have equal talent. But, all of us have an equal opportunity to develop our talents.", author: "APJ Abdul Kalam" },
    { text: "Don't take rest after your first victory because if you fail in second, more lips are waiting to say that your first victory was just luck.", author: "APJ Abdul Kalam" },
    { text: "Excellence is a continuous process and not an accident.", author: "APJ Abdul Kalam" },
    { text: "Learning gives creativity, creativity leads to thinking, thinking provides knowledge, knowledge makes you great.", author: "APJ Abdul Kalam" },
    
    // Final Push
    { text: "The only thing standing between you and your goal is the story you keep telling yourself.", author: "Jordan Belfort" },
    { text: "Don't stop when you're tired. Stop when you're done.", author: "Unknown" },
    { text: "The pain you feel today will be the strength you feel tomorrow.", author: "Unknown" },
    { text: "Success is not for the lazy.", author: "Unknown" },
    { text: "Work hard in silence, let your success be your noise.", author: "Frank Ocean" },
    { text: "Stay hungry, stay foolish.", author: "Steve Jobs" },
    { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
    { text: "Don't wish it were easier. Wish you were better.", author: "Jim Rohn" },
    { text: "Great things are done by a series of small things brought together.", author: "Vincent Van Gogh" },
    { text: "Start where you are. Use what you have. Do what you can.", author: "Arthur Ashe" },
  ];

  useEffect(() => {
    loadDashboardData();
    loadMotivationQuote();
  }, []);

  const loadMotivationQuote = () => {
    // Show a random quote on every page load/refresh
    const randomQuote = motivationQuotes[Math.floor(Math.random() * motivationQuotes.length)];
    setMotivationQuote(randomQuote);
  };

  const loadDashboardData = async () => {
    try {
      const [challengeRes, testsRes] = await Promise.all([
        dailyChallengeAPI.get(),
        testAPI.getHistory(),
      ]);
      setDailyChallenge(challengeRes.data);
      setRecentTests(testsRes.data.slice(0, 3));
      
      // Calculate stats — compare using local date strings for reliable matching
      const todayStr = new Date().toLocaleDateString();
      
      const todayTests = testsRes.data.filter(t => {
        const dateStr = t.completed_at || t.created_at;
        if (!dateStr) return false;
        return new Date(dateStr).toLocaleDateString() === todayStr;
      });
      
      // Calculate accuracy from today's tests
      let todayCorrect = 0;
      let todayTotal = 0;
      todayTests.forEach(t => {
        todayCorrect += (t.score || 0);
        todayTotal += (t.total || 0);
      });
      
      const accuracy = todayTotal > 0 ? Math.round((todayCorrect / todayTotal) * 100) : 0;
      
      setStats({
        testsToday: todayTests.length,
        accuracy: accuracy,
        totalTests: testsRes.data.length,
        questionsToday: todayTotal,
        correctToday: todayCorrect
      });
    } catch (error) {
      logger.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExam = (exam) => {
    // Find the exam object to check availability
    const examObj = examsList.find(e => e.id === exam);
    if (examObj && !examObj.available) {
      toast.info(`${examObj.name} is coming soon!`, {
        description: "We're working on adding this exam.",
        duration: 3000
      });
      return;
    }
    setSelectedExam(exam);
    localStorage.setItem('selectedExam', exam);
  };

  const examsList = [
    { id: 'jee-main', name: 'JEE Main', available: true },
    { id: 'jee-adv', name: 'JEE Advanced', available: false },
    { id: 'neet', name: 'NEET UG', available: true },
    { id: 'cuet', name: 'CUET', available: false },
  ];

  const quickActions = [
    { 
      icon: Target, 
      label: 'Quick Test', 
      desc: '10 questions, 15 min',
      path: '/test/create', 
      gradient: 'from-primary to-primary/70',
      action: () => navigate('/test/create')
    },
    { 
      icon: Zap, 
      label: 'Practice Mode', 
      desc: 'Endless practice',
      path: '/practice', 
      gradient: 'from-green-500 to-green-600',
      action: () => navigate('/practice')
    },
    { 
      icon: Bookmark, 
      label: 'Bookmarks', 
      desc: 'Saved questions',
      path: '/bookmarks', 
      gradient: 'from-amber-500 to-amber-600',
      action: () => navigate('/bookmarks')
    },
    { 
      icon: BarChart3, 
      label: 'Analytics', 
      desc: 'Track progress',
      path: '/analytics', 
      gradient: 'from-indigo-500 to-indigo-600',
      action: () => navigate('/analytics')
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header - Cozy Student Style */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-full mb-4">
                <Coffee className="text-amber-600 dark:text-amber-400" size={18} />
                <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Ready to learn</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">
                Welcome back, <span className="text-primary">{user?.name}</span>! 📚
              </h1>
              <p className="text-lg text-muted-foreground mt-2">Let's make today productive!</p>
            </div>
            <div className="hidden md:flex flex-col items-center p-4 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-700/50 rounded-2xl">
              <GraduationCap className="text-violet-600 dark:text-violet-400 mb-1" size={24} />
              <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">Level {Math.floor((user?.points || 0) / 100) + 1}</p>
              <p className="text-xs text-muted-foreground">Keep going</p>
            </div>
          </div>
        </div>

        {/* Daily Motivation Quote - Below Welcome, Above Exam Selection */}
        {motivationQuote && (
          <Card className="mb-8 p-6 bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/20 dark:to-orange-900/20 text-foreground border border-rose-200 dark:border-rose-800/50 rounded-2xl shadow-sm animate-fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-rose-100 dark:bg-rose-800/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <Heart className="text-rose-500 dark:text-rose-400" size={24} />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-rose-600 dark:text-rose-400 mb-2 uppercase tracking-wider">Daily Inspiration</p>
                <p className="text-base font-medium italic mb-2 leading-relaxed text-foreground">"{motivationQuote.text}"</p>
                <p className="text-muted-foreground text-sm">— {motivationQuote.author}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Select Your Exam Section - Friendly Style */}
        {!selectedExam && (
          <Card className="mb-8 p-8 bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20 border border-sky-200 dark:border-sky-800/50 rounded-2xl shadow-sm stagger-item">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-heading font-bold text-foreground mb-2 flex items-center gap-3">
                  <BookMarked className="text-sky-600 dark:text-sky-400" size={32} />
                  Choose Your Exam
                </h2>
                <p className="text-muted-foreground text-base">Select your target exam to get personalized practice</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {examsList.map((exam, index) => {
                const ExamIcon = getExamIcon(exam.name);
                const isAvailable = exam.available;
                return (
                <button
                  key={exam.id}
                  onClick={() => handleSelectExam(exam.id)}
                  className={`group relative p-6 bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-2xl transition-all duration-200 text-left ${
                    isAvailable 
                      ? 'hover:border-primary hover:bg-primary/5 hover:shadow-md cursor-pointer' 
                      : 'opacity-70 border-dashed cursor-pointer'
                  }`}
                  style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                >
                  {/* Coming Soon Badge */}
                  {!isAvailable && (
                    <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white border-0 text-[10px] px-2 py-0.5 flex items-center gap-1">
                      <Clock size={10} />
                      Soon
                    </Badge>
                  )}
                  <div className={`w-14 h-14 bg-gradient-to-br from-sky-100 to-indigo-100 dark:from-sky-800/30 dark:to-indigo-800/30 rounded-xl flex items-center justify-center mb-3 transition-all ${isAvailable ? 'group-hover:scale-105' : 'opacity-60'}`}>
                    <ExamIcon className={`${isAvailable ? 'text-sky-600 dark:text-sky-400 group-hover:text-primary' : 'text-muted-foreground'}`} size={28} />
                  </div>
                  <h3 className={`font-heading font-bold text-sm transition-colors ${isAvailable ? 'text-foreground group-hover:text-primary' : 'text-muted-foreground'}`}>{exam.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    {isAvailable ? (
                      <>
                        <Pencil size={12} className="text-sky-500" />
                        Get started
                      </>
                    ) : (
                      <>
                        <Lock size={12} className="text-amber-500" />
                        Coming soon
                      </>
                    )}
                  </p>
                </button>
              );
              })}
            </div>
          </Card>
        )}

        {selectedExam && (
          <Card className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl border border-emerald-200 dark:border-emerald-800/50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-800/30 rounded-xl flex items-center justify-center">
                <Target className="text-emerald-600 dark:text-emerald-400" size={24} />
              </div>
              <div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wider mb-1">📖 Your Focus</p>
                <h3 className="text-lg font-bold text-foreground">{examsList.find(e => e.id === selectedExam)?.name}</h3>
              </div>
            </div>
            <Button
              onClick={() => {
                setSelectedExam(null);
                localStorage.removeItem('selectedExam');
              }}
              variant="outline"
              size="sm"
              className="border-emerald-300 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-800/30"
            >
              Change Exam
            </Button>
          </Card>
        )}

        {/* Stats Grid - Cozy Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Points Card */}
          <Card className="p-6 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 text-foreground border border-violet-200 dark:border-violet-800/50 rounded-2xl shadow-sm stagger-item hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-violet-100 dark:bg-violet-800/30 rounded-xl flex items-center justify-center">
                <Star className="text-amber-500" size={28} />
              </div>
              <div>
                <p className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">⭐ Total Points</p>
                <p className="text-3xl font-heading font-bold text-foreground">{user?.points || 0}</p>
              </div>
            </div>
            <div className="w-full h-2 bg-violet-200 dark:bg-violet-800/50 rounded-full overflow-hidden mb-3">
              <div 
                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-700"
                style={{ width: `${((user?.points || 0) % 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground">{100 - ((user?.points || 0) % 100)} points to next level</p>
            <Button 
              onClick={() => navigate('/profile')}
              className="w-full mt-4 bg-violet-100 hover:bg-violet-200 dark:bg-violet-800/30 dark:hover:bg-violet-800/50 text-violet-700 dark:text-violet-300 font-medium rounded-xl"
            >
              View Profile
            </Button>
          </Card>

          {/* Streak Card */}
          <Card className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 text-foreground border border-orange-200 dark:border-orange-800/50 rounded-2xl shadow-sm stagger-item hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-800/30 rounded-xl flex items-center justify-center">
                <Flame className="text-orange-500" size={28} />
              </div>
              <div>
                <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider">Study Streak</p>
                <p className="text-3xl font-heading font-bold text-foreground">{user?.streak || 0} days</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-orange-600 dark:text-orange-400 font-medium">Keep it going</span>
              {(user?.streak || 0) >= 7 && <span className="px-2 py-1 bg-orange-100 dark:bg-orange-800/30 rounded-full text-xs text-orange-600 dark:text-orange-400 font-medium">7-day streak</span>}
            </div>
          </Card>

          {/* Tests Today */}
          <Card className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 text-foreground border border-emerald-200 dark:border-emerald-800/50 rounded-2xl shadow-sm stagger-item hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-800/30 rounded-xl flex items-center justify-center">
                <Target className="text-emerald-500" size={28} />
              </div>
              <div>
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Today's Progress</p>
                <p className="text-3xl font-heading font-bold text-foreground">{stats.testsToday}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">Tests completed today</p>
          </Card>
        </div>

        {/* Quick Actions - Friendly Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-6 flex items-center gap-3">
            <Zap className="text-amber-500" size={28} />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              const cardColors = [
                'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800/50 hover:border-sky-400',
                'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50 hover:border-emerald-400',
                'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 hover:border-amber-400',
                'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/50 hover:border-violet-400'
              ];
              const iconColors = ['text-sky-600 dark:text-sky-400', 'text-emerald-600 dark:text-emerald-400', 'text-amber-600 dark:text-amber-400', 'text-violet-600 dark:text-violet-400'];
              const iconBgs = ['bg-sky-100 dark:bg-sky-800/30', 'bg-emerald-100 dark:bg-emerald-800/30', 'bg-amber-100 dark:bg-amber-800/30', 'bg-violet-100 dark:bg-violet-800/30'];
              
              return (
                <Card
                  key={index}
                  onClick={action.action}
                  className={`group p-6 ${cardColors[index]} rounded-2xl hover:shadow-md transition-all duration-200 cursor-pointer border interactive-card stagger-item`}
                  style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                >
                  <div className={`w-14 h-14 ${iconBgs[index]} rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-all`}>
                    <Icon className={`${iconColors[index]}`} size={28} />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-1 text-lg">{action.label}</h3>
                  <p className="text-sm text-muted-foreground">{action.desc}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className={iconColors[index]}>Get started</span>
                    <ArrowRight className={iconColors[index]} size={14} />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Daily Challenge - Warm & Inviting */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="lg:col-span-2 p-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-2xl border border-amber-200 dark:border-amber-800/50 shadow-sm hover:shadow-md transition-all duration-300 stagger-item relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 dark:bg-amber-700/10 rounded-full -mr-16 -mt-16"></div>
            
            <div className="flex items-start justify-between flex-wrap gap-6 relative z-10">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-800/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="text-amber-500" size={32} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-heading font-bold text-foreground">Daily Challenge</h2>
                    <span className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-800/30 rounded-full text-amber-700 dark:text-amber-400">+50 pts</span>
                  </div>
                  <p className="text-muted-foreground text-sm">5 curated questions • Boost your streak</p>
                </div>
              </div>
              {dailyChallenge?.completed_by_user ? (
                <span className="px-4 py-2 bg-emerald-100 dark:bg-emerald-800/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium rounded-full flex items-center gap-2">
                  <Radio size={16} />
                  Completed ✓
                </span>
              ) : (
                <Button
                  onClick={() => navigate('/test/create')}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-medium px-6 rounded-xl shadow-sm"
                >
                  Start Challenge
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              )}
            </div>
          </Card>

          {/* Today's Stats */}
          <Card className="p-6 bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20 rounded-2xl border border-sky-200 dark:border-sky-800/50 shadow-sm stagger-item">
            <h3 className="font-heading font-bold text-foreground mb-4 flex items-center gap-2 text-lg">
              <TrendingUp className="text-sky-600 dark:text-sky-400" size={22} />
              Today's Stats
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Accuracy</span>
                  <span className="text-lg font-bold text-sky-600 dark:text-sky-400">{stats.accuracy}%</span>
                </div>
                <div className="w-full h-3 bg-sky-100 dark:bg-sky-800/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-all duration-700"
                    style={{ width: `${stats.accuracy}%` }}
                  ></div>
                </div>
              </div>
              <div className="pt-4 border-t border-sky-200 dark:border-sky-800/50">
                <Button 
                  onClick={() => navigate('/analytics')}
                  className="w-full bg-sky-100 hover:bg-sky-200 dark:bg-sky-800/30 dark:hover:bg-sky-800/50 text-sky-700 dark:text-sky-300 font-medium rounded-xl"
                >
                  View Analytics
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Activity - Friendly Style */}
        {recentTests.length > 0 && (
          <div className="stagger-item">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-3">
                <BookOpen className="text-violet-600 dark:text-violet-400" size={28} />
                Recent Practice
              </h2>
              <Button
                variant="ghost"
                onClick={() => navigate('/analytics')}
                className="text-violet-600 dark:text-violet-400 font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20"
              >
                View All
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recentTests.map((test, index) => {
                const percentage = (test.score / test.total) * 100;
                let cardStyle = 'border-rose-200 dark:border-rose-800/50 bg-rose-50 dark:bg-rose-900/20';
                let statusText = 'Needs practice';
                let statusColor = 'text-rose-600 dark:text-rose-400';
                let iconBg = 'bg-rose-100 dark:bg-rose-800/30';
                
                if (percentage >= 70) {
                  cardStyle = 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20';
                  statusText = 'Great job!';
                  statusColor = 'text-emerald-600 dark:text-emerald-400';
                  iconBg = 'bg-emerald-100 dark:bg-emerald-800/30';
                } else if (percentage >= 50) {
                  cardStyle = 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20';
                  statusText = 'Good effort!';
                  statusColor = 'text-amber-600 dark:text-amber-400';
                  iconBg = 'bg-amber-100 dark:bg-amber-800/30';
                }
                
                return (
                  <Card
                    key={test.id}
                    className={`p-6 rounded-2xl cursor-pointer border ${cardStyle} hover:shadow-md group interactive-card stagger-item transition-all`}
                    onClick={() => navigate(`/test/${test.id}/results`)}
                    style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform`}>
                        <BookOpen className="text-foreground" size={28} />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor} bg-white/50 dark:bg-gray-900/50`}>
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground text-lg mb-2 capitalize group-hover:text-primary transition-colors">
                      {test.test_type} Test
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Calendar size={14} />
                      <span>{new Date(test.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="pt-4 border-t border-current/10 flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Score</span>
                      <span className={`text-2xl font-heading font-bold ${statusColor}`}>{test.score}/{test.total}</span>
                    </div>
                    <p className={`text-xs font-medium mt-2 ${statusColor}`}>{statusText}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State - Friendly & Encouraging */}
        {recentTests.length === 0 && !loading && (
          <Card className="p-16 text-center bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20 rounded-2xl border-2 border-dashed border-sky-300 dark:border-sky-700 stagger-item">
            <div className="w-20 h-20 bg-sky-100 dark:bg-sky-800/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BookOpen className="text-sky-600 dark:text-sky-400" size={40} />
            </div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-3">Start Your Journey</h3>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto text-lg">Take your first test to begin tracking your progress and building good study habits.</p>
            <Button 
              onClick={() => navigate('/test/create')} 
              className="bg-sky-500 hover:bg-sky-600 text-white font-medium px-8 py-3 rounded-xl shadow-sm"
            >
              <Pencil className="mr-2" size={20} />
              Start Your First Test
            </Button>
          </Card>
        )}

        {/* Discord Community Banner */}
        <Card className="mt-8 p-6 rounded-2xl border-0 shadow-lg overflow-hidden animate-fade-in" style={{background: 'linear-gradient(135deg, #5865F2, #4752C4)'}}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <MessageCircle className="text-white" size={32} />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                Join the ExamDex Community
              </h3>
              <p className="text-white/80">
                Get study tips, share doubts, and help your peers.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white font-bold rounded-xl hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:scale-105" style={{color: '#5865F2'}}
              >
                <MessageCircle size={20} />
                Join Discord
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
